'use client';

import React, { useId, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Chip, Text } from '@/components/ds';
import type { NewsReference } from '@/api/news/types';
import { formatDocDate, sourceTint } from '@/components/sources/sourceMeta';
import { articleNumber, selectionForReference } from '@/lib/news/newsUtils';
import { cn } from '@/lib/twUtils';
import { useOpenNewsDoc } from './NewsDocPreview';

const Initials = ({ reference }: { reference: NewsReference }) => {
  const selection = selectionForReference(reference);
  return (
    <span
      aria-hidden
      className={cn(
        'grid h-9 w-9 shrink-0 place-items-center rounded-ds-sm text-[0.6875rem] font-bold',
        sourceTint(selection.sourceId)
      )}
    >
      {selection.sourceInitials}
    </span>
  );
};

const linkClass =
  'rounded-sm font-semibold text-primary underline decoration-hairline underline-offset-[3px] transition-colors ds-focus hover:text-accent-600 hover:decoration-accent';

/**
 * Il provvedimento che fa notizia, in testa all'articolo: la scheda è a un clic
 * prima ancora di cominciare a leggere.
 */
export const NewsPrimarySource = ({ reference }: { reference: NewsReference }) => {
  const openDoc = useOpenNewsDoc();
  const selection = selectionForReference(reference);

  return (
    <div className="flex flex-col gap-3.5 rounded-ds-lg border border-edge bg-white p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3.5">
        <Initials reference={reference} />
        <div className="min-w-0">
          <p className="text-[0.6875rem] font-semibold tracking-[0.1em] text-ink-faint uppercase">
            La fonte · {selection.sourceName}
          </p>
          <p className="mt-0.5 font-display text-[0.96875rem] leading-[1.35] font-semibold text-primary">
            {selection.documentName}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => openDoc({ publicId: reference.public_id, anchor: null })}
        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-sm pl-[3.125rem] text-[0.875rem] font-semibold text-accent ds-focus hover:text-accent-600 sm:self-auto sm:pl-0"
      >
        Apri la scheda
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

/** Oltre queste, le fonti restano chiuse dietro «Mostra tutte le N fonti». */
const VISIBLE_REFERENCES = 5;

/**
 * Tutte le fonti citate, una riga per documento: il nome apre la scheda, e se
 * sono citati articoli precisi seguono « - art. 360» o « - artt. 44, 81, 115»,
 * ciascun numero col suo link all'articolo. Gli articoli arrivano dal backend
 * già in ordine crescente e senza doppioni.
 *
 * Il provvedimento che fa notizia va in testa, poi le altre in ordine di prima
 * citazione. Oltre la quinta le righe restano nell'HTML (per chi indicizza) ma
 * con `hidden`: non si vedono e non si raggiungono col tab finché non si apre
 * l'elenco.
 */
const NewsReferences = ({ references }: { references: NewsReference[] }) => {
  const openDoc = useOpenNewsDoc();
  const listId = useId();
  const [expanded, setExpanded] = useState(false);
  if (references.length === 0) return null;

  const ordered = [
    ...references.filter(reference => reference.is_primary),
    ...references.filter(reference => !reference.is_primary),
  ];
  const collapsible = ordered.length > VISIBLE_REFERENCES;
  const visibleCount = collapsible && !expanded ? VISIBLE_REFERENCES : ordered.length;

  return (
    <section aria-labelledby="fonti-citate">
      <h2
        id="fonti-citate"
        className="scroll-mt-26 font-display text-[1.25rem] leading-tight font-semibold tracking-[-0.02em] text-primary"
      >
        Fonti citate
      </h2>
      <Text size="small" className="mt-1.5">
        Tutte le fonti citate nell’articolo sono consultabili direttamente. Apri la scheda per
        leggere il documento e verificarlo alla fonte.
      </Text>
      <ol id={listId} className="mt-4 overflow-hidden rounded-ds-lg border border-edge bg-white">
        {ordered.map((reference, index) => {
          const selection = selectionForReference(reference);
          return (
            <li
              key={reference.public_id}
              hidden={index >= visibleCount}
              className={cn(
                'flex items-start gap-3.5 px-4 py-3',
                // Il filetto separa le righe visibili: l'ultima aperta non lo ha
                index < visibleCount - 1 && 'border-b border-edge-soft'
              )}
            >
              <Initials reference={reference} />
              <div className="min-w-0 flex-1 text-[0.90625rem] leading-[1.5] text-ink-strong">
                <button
                  type="button"
                  onClick={() => openDoc({ publicId: reference.public_id, anchor: null })}
                  className={cn(linkClass, 'text-left')}
                >
                  {selection.documentName}
                </button>
                {reference.anchors.length > 0 && (
                  <>
                    {' - '}
                    {/* Un solo articolo: il link prende anche «art.», che da solo
                        sarebbe un pezzo di testo morto accanto al numero */}
                    {reference.anchors.length === 1 ? (
                      <button
                        type="button"
                        onClick={() =>
                          openDoc({ publicId: reference.public_id, anchor: reference.anchors[0] })
                        }
                        aria-label={`${selection.documentName}, ${reference.anchors[0]}`}
                        className={linkClass}
                      >
                        art. {articleNumber(reference.anchors[0])}
                      </button>
                    ) : (
                      <>
                        {'artt. '}
                        {reference.anchors.map((anchor, index) => (
                          <React.Fragment key={anchor}>
                            {index > 0 && ', '}
                            <button
                              type="button"
                              onClick={() => openDoc({ publicId: reference.public_id, anchor })}
                              aria-label={`${selection.documentName}, ${anchor}`}
                              className={linkClass}
                            >
                              {articleNumber(anchor)}
                            </button>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </>
                )}
                {reference.date && (
                  <span className="block text-[0.78125rem] text-ink-subtle">
                    {formatDocDate(reference.date)}
                  </span>
                )}
              </div>
              {reference.is_primary && (
                <Chip tone="accent" size="xs" className="hidden shrink-0 sm:inline-flex">
                  La notizia
                </Chip>
              )}
            </li>
          );
        })}
      </ol>
      {collapsible && !expanded && (
        <button
          type="button"
          aria-expanded={false}
          aria-controls={listId}
          onClick={() => setExpanded(true)}
          className="group/arrow mt-3.5 inline-flex items-center gap-1.5 rounded-sm text-[0.875rem] font-semibold text-accent ds-focus hover:text-accent-600"
        >
          Mostra tutte le {ordered.length} fonti
          <span
            aria-hidden
            className="transition-transform duration-150 group-hover/arrow:translate-x-0.5 motion-reduce:transition-none"
          >
            →
          </span>
        </button>
      )}
    </section>
  );
};

export default NewsReferences;
